import { useState } from "react";
import { Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useUpsertActionPlanAction } from "src/api/client-api/ActionPlan/ActionPlanAction/useUpsertActionPlanAction";
import { useActionsContext } from "src/context/Actions/useActionsContext";
import { useCatalogContext } from "../Summary/context/useCatalogContext";
import { makePostIRI } from "src/utils/actionUtils";
import { useSnackbar } from "src/utils/useSnackbar";
import { getActionStatus } from "src/components/Actions/Common/actionStatus";
import { useNavigate } from "react-router";
import { isAxiosError } from "axios";
import { extractValidationMessages } from "src/utils/validation";
import { ValidationMessage } from "src/components/QuestionnaireForm/FormProperty/validationTypes";
import { ActionViolationErrorsModal } from "../../ActionViolationErrorsModal/ActionViolationErrorsModal";

export const AddActionButton = () => {
    const { t } = useTranslation();
    const snackbar = useSnackbar();
    const navigate = useNavigate();
    const [validationMessages, setValidationMessages] = useState<ValidationMessage[]>([]);
    const [isValidationModalOpen, setIsValidationModalOpen] = useState(false);

    const { actionPlan, urls, savingStatus, lastSelectedVariation } = useActionsContext();
    const simulationSsqPosts = actionPlan?.simulationSsqPosts;
    const firstPostWithActionPlan = simulationSsqPosts?.[0];
    const targetPostWithActionPlan = simulationSsqPosts?.[simulationSsqPosts.length - 1];

    const { currentAction } = useCatalogContext();

    const createActionPlanAction = useUpsertActionPlanAction({ actionPlanID: actionPlan?.id });

    if (currentAction === undefined || actionPlan === null) {
        return null;
    }

    const handleAddToPlan = () => {
        savingStatus.setSaving(true);

        const defaultStatus = getActionStatus(t)[1];
        const payload = {
            actionId: currentAction.id,
            actionPlan: actionPlan?.["@id"],
            type: defaultStatus.backendLabel,
            startSimulationSsqPost: makePostIRI(firstPostWithActionPlan?.id),
            endSimulationSsqPost: makePostIRI(targetPostWithActionPlan?.id),
        };

        createActionPlanAction.mutate(payload, {
            onSuccess: (response) => {
                if (response?.data?.id) {
                    // Naviguer vers l'édition de l'action nouvellement créée
                    snackbar.forceClose();
                    snackbar.setSnackbarProps({
                        type: "success",
                        message: t("action_plan.action_added_to_plan", "Action added to plan successfully!"),
                    });

                    const newActionId = response.data.id;
                    const url = urls.editPlanning(newActionId);
                    navigate(url);
                }
            },
            onError: (error) => {
                snackbar.forceClose();

                if (isAxiosError(error)) {
                    const statusCode = error.response?.status;
                    if (statusCode === 422) {
                        const violationMessages = extractValidationMessages(error.response?.data);
                        if (violationMessages.length > 0) {
                            setValidationMessages(violationMessages);
                            setIsValidationModalOpen(true);

                            snackbar.setSnackbarProps({
                                type: "error",
                                message: t("action_plan.error_adding_action_to_plan", "Error adding action to plan"),
                            });

                            return;
                        }
                    }
                }

                snackbar.setSnackbarProps({
                    type: "error",
                    forceAutoHideInMilisecond: 3000,
                    message: `${t("action_plan.error_adding_action_to_plan", "Error adding action to plan")}: ${isAxiosError(error) ? error.response?.data?.description : ""}`,
                });
            },
            onSettled: () => {
                savingStatus.setSaving(false);
            },
        });
    };

    return (
        <>
            <Button
                variant="primary"
                disabled={createActionPlanAction.isLoading}
                onClick={() => {
                    handleAddToPlan();
                }}
            >
                {createActionPlanAction.isLoading ?
                    t("action_plan.adding_action", "Adding action...")
                :   t("action_plan.add_to_plan", "Add to plan")}
            </Button>

            <ActionViolationErrorsModal
                isOpen={isValidationModalOpen}
                onClose={() => setIsValidationModalOpen(false)}
                validationMessages={validationMessages}
                actionPlan={actionPlan}
                scoreId={lastSelectedVariation.scoreId}
            />
        </>
    );
};
